import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calculator as CalcIcon, Save, Copy, Sparkles, FileText, Send, Trash2,
  Bot, User, Loader2, Delete,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// Unit conversion to feet (we normalize everything to feet, then feet->yards)
const TO_FEET = { ft: 1, in: 1 / 12, yd: 3 };

const MATERIALS = ["Concrete", "Dirt", "Gravel", "Stone", "Sand", "Mulch", "Topsoil", "Other"];

const SUGGESTED_PROMPTS = [
  "How many sheets of 5x10 mesh are needed for 1,000 sq ft?",
  "How many tons of gravel does 12 cubic yards equal?",
  "How much topsoil do I need for a 20x30 yard, 4 inches deep?",
  "Convert 400 cubic feet to cubic yards.",
  "How much stone is needed for a 30x40 area at 6 inches?",
  "What's a fair labor rate for a 1,200 sq ft concrete pour?",
];

// -------- Standard calculator --------
function StandardCalc() {
  const [display, setDisplay] = useState("0");
  const [prev, setPrev] = useState(null);
  const [op, setOp] = useState(null);
  const [waitNext, setWaitNext] = useState(false);
  const [memory, setMemory] = useState(0);

  const press = (v) => {
    if (waitNext || display === "0") {
      setDisplay(v === "." ? "0." : String(v));
      setWaitNext(false);
    } else {
      if (v === "." && display.includes(".")) return;
      setDisplay(display + v);
    }
  };
  const clear = () => { setDisplay("0"); setPrev(null); setOp(null); setWaitNext(false); };
  const backspace = () => {
    if (waitNext) return;
    setDisplay(display.length > 1 ? display.slice(0, -1) : "0");
  };
  const compute = (a, b, operator) => {
    const x = Number(a), y = Number(b);
    switch (operator) {
      case "+": return x + y;
      case "-": return x - y;
      case "×": return x * y;
      case "÷": return y === 0 ? NaN : x / y;
      default: return y;
    }
  };
  const setOperator = (next) => {
    if (prev == null) {
      setPrev(display);
    } else if (!waitNext) {
      const result = compute(prev, display, op);
      setPrev(String(result));
      setDisplay(String(result));
    }
    setOp(next);
    setWaitNext(true);
  };
  const equals = () => {
    if (op == null || prev == null) return;
    const result = compute(prev, display, op);
    setDisplay(String(result));
    setPrev(null); setOp(null); setWaitNext(true);
  };
  const percent = () => {
    setDisplay(String(Number(display) / 100));
  };
  const negate = () => {
    if (display === "0") return;
    setDisplay(display.startsWith("-") ? display.slice(1) : "-" + display);
  };

  const btn =
    "h-12 rounded-lg font-semibold text-lg transition-all active:scale-95 shadow-sm border";
  const num = `${btn} bg-white border-slate-200 hover:bg-slate-50 text-slate-900`;
  const act = `${btn} bg-slate-100 border-slate-200 hover:bg-slate-200 text-slate-900`;
  const eq = `${btn} bg-blue-600 border-blue-700 hover:bg-blue-700 text-white`;
  const op_ = (active) =>
    `${btn} ${active ? "bg-blue-600 text-white border-blue-700" : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"}`;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="standard-calc">
      <div className="mb-4 bg-slate-900 text-white rounded-lg px-4 py-5 text-right">
        <div className="text-xs opacity-60 h-4" data-testid="calc-history">
          {prev != null ? `${prev} ${op || ""}` : (memory !== 0 ? `M = ${memory}` : "")}
        </div>
        <div className="font-mono text-3xl sm:text-4xl font-bold truncate" data-testid="calc-display">
          {display}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 text-sm">
        <button className={act} onClick={() => setMemory(0)} data-testid="calc-mc">MC</button>
        <button className={act} onClick={() => setDisplay(String(memory))} data-testid="calc-mr">MR</button>
        <button className={act} onClick={() => setMemory(memory + Number(display))} data-testid="calc-mplus">M+</button>
        <button className={act} onClick={() => setMemory(memory - Number(display))} data-testid="calc-mminus">M−</button>

        <button className={act} onClick={clear} data-testid="calc-clear">AC</button>
        <button className={act} onClick={negate}>±</button>
        <button className={act} onClick={percent}>%</button>
        <button className={op_(op === "÷")} onClick={() => setOperator("÷")} data-testid="calc-div">÷</button>

        <button className={num} onClick={() => press("7")}>7</button>
        <button className={num} onClick={() => press("8")}>8</button>
        <button className={num} onClick={() => press("9")}>9</button>
        <button className={op_(op === "×")} onClick={() => setOperator("×")} data-testid="calc-mul">×</button>

        <button className={num} onClick={() => press("4")}>4</button>
        <button className={num} onClick={() => press("5")}>5</button>
        <button className={num} onClick={() => press("6")}>6</button>
        <button className={op_(op === "-")} onClick={() => setOperator("-")} data-testid="calc-sub">−</button>

        <button className={num} onClick={() => press("1")}>1</button>
        <button className={num} onClick={() => press("2")}>2</button>
        <button className={num} onClick={() => press("3")}>3</button>
        <button className={op_(op === "+")} onClick={() => setOperator("+")} data-testid="calc-add">+</button>

        <button className={`${num} col-span-2`} onClick={() => press("0")}>0</button>
        <button className={num} onClick={() => press(".")}>.</button>
        <button className={eq} onClick={equals} data-testid="calc-eq">=</button>

        <button className={`${act} col-span-4 flex items-center justify-center gap-2`} onClick={backspace}>
          <Delete className="w-4 h-4" /> Backspace
        </button>
      </div>
    </div>
  );
}

// -------- Cubic Yard Calculator --------
function CubicYardCalc({ onCreateQuote, onSave, onAnalyze }) {
  const [length, setLength] = useState("30");
  const [width, setWidth] = useState("40");
  const [depth, setDepth] = useState("4");
  const [lUnit, setLUnit] = useState("ft");
  const [wUnit, setWUnit] = useState("ft");
  const [dUnit, setDUnit] = useState("in");
  const [material, setMaterial] = useState("Concrete");

  const results = useMemo(() => {
    const L = parseFloat(length) * (TO_FEET[lUnit] || 1);
    const W = parseFloat(width) * (TO_FEET[wUnit] || 1);
    const D = parseFloat(depth) * (TO_FEET[dUnit] || 1);
    const safe = (x) => (Number.isFinite(x) ? x : 0);
    const sq = safe(L * W);
    const cf = safe(L * W * D);
    const cy = safe(cf / 27);
    return { sq, cf, cy, L, W, D };
  }, [length, width, depth, lUnit, wUnit, dUnit]);

  const copyResults = () => {
    const text =
      `${material} — ${length} ${lUnit} × ${width} ${wUnit} × ${depth} ${dUnit}\n` +
      `Square Feet: ${results.sq.toLocaleString(undefined, { maximumFractionDigits: 2 })}\n` +
      `Cubic Feet: ${results.cf.toLocaleString(undefined, { maximumFractionDigits: 2 })}\n` +
      `Cubic Yards: ${results.cy.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    navigator.clipboard.writeText(text);
    toast.success("Results copied");
  };

  const UnitSel = ({ value, onChange, tid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-11 w-20" data-testid={tid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="ft">ft</SelectItem>
        <SelectItem value="in">in</SelectItem>
        <SelectItem value="yd">yd</SelectItem>
      </SelectContent>
    </Select>
  );

  const payload = {
    length: parseFloat(length) || 0,
    width: parseFloat(width) || 0,
    depth: parseFloat(depth) || 0,
    length_unit: lUnit, width_unit: wUnit, depth_unit: dUnit,
    square_feet: Number(results.sq.toFixed(2)),
    cubic_feet: Number(results.cf.toFixed(2)),
    cubic_yards: Number(results.cy.toFixed(2)),
    material,
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="cy-calc">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-md bg-blue-600 flex items-center justify-center">
          <CalcIcon className="w-4 h-4 text-white" />
        </div>
        <h2 className="font-display text-xl font-extrabold text-slate-900">Cubic Yard Calculator</h2>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div>
          <Label>Length</Label>
          <div className="flex gap-2 mt-1">
            <Input type="number" inputMode="decimal" value={length} onChange={(e) => setLength(e.target.value)} className="h-11" data-testid="cy-length" />
            <UnitSel value={lUnit} onChange={setLUnit} tid="cy-length-unit" />
          </div>
        </div>
        <div>
          <Label>Width</Label>
          <div className="flex gap-2 mt-1">
            <Input type="number" inputMode="decimal" value={width} onChange={(e) => setWidth(e.target.value)} className="h-11" data-testid="cy-width" />
            <UnitSel value={wUnit} onChange={setWUnit} tid="cy-width-unit" />
          </div>
        </div>
        <div>
          <Label>Depth</Label>
          <div className="flex gap-2 mt-1">
            <Input type="number" inputMode="decimal" value={depth} onChange={(e) => setDepth(e.target.value)} className="h-11" data-testid="cy-depth" />
            <UnitSel value={dUnit} onChange={setDUnit} tid="cy-depth-unit" />
          </div>
        </div>
      </div>

      <div className="mb-4">
        <Label>Material (for your records)</Label>
        <Select value={material} onValueChange={setMaterial}>
          <SelectTrigger className="h-11 mt-1" data-testid="cy-material"><SelectValue /></SelectTrigger>
          <SelectContent>
            {MATERIALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
          <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Square Feet</div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1" data-testid="cy-sq">
            {results.sq.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
          <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Cubic Feet</div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1" data-testid="cy-cf">
            {results.cf.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
          <div className="text-[10px] uppercase font-bold text-blue-700 tracking-wider">Cubic Yards</div>
          <div className="font-display text-2xl font-extrabold text-blue-700 mt-1" data-testid="cy-cy">
            {results.cy.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <Button variant="outline" onClick={copyResults} className="h-11" data-testid="cy-copy">
          <Copy className="w-4 h-4 mr-2" /> Copy Results
        </Button>
        <Button variant="outline" onClick={() => onSave(payload)} className="h-11" data-testid="cy-save">
          <Save className="w-4 h-4 mr-2" /> Save Calculation
        </Button>
        <Button variant="outline" onClick={() => onAnalyze(payload)} className="h-11" data-testid="cy-analyze">
          <Sparkles className="w-4 h-4 mr-2" /> Analyze Results
        </Button>
        <Button onClick={() => onCreateQuote(payload)} className="h-11 bg-blue-600 hover:bg-blue-700" data-testid="cy-create-quote">
          <FileText className="w-4 h-4 mr-2" /> Create Quote From Results
        </Button>
      </div>
    </div>
  );
}

// -------- History panel --------
function HistoryPanel({ items, onOpen, onQuote, onDelete }) {
  if (!items.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 text-center text-sm text-slate-500" data-testid="calc-history-empty">
        No saved calculations yet. Click <span className="font-semibold">Save Calculation</span> to keep one here.
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="calc-history-list">
      <div className="px-5 py-3 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
        Recent Calculations
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((c) => (
          <li key={c.id} className="px-5 py-3 flex items-center gap-3" data-testid={`calc-row-${c.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate">{c.project_name}</div>
              <div className="text-xs text-slate-500">
                {c.material} · {c.length}{c.length_unit} × {c.width}{c.width_unit} × {c.depth}{c.depth_unit} ·{" "}
                <span className="font-mono">{c.cubic_yards.toFixed(2)} cu yd</span> · {(c.created_at || "").slice(0, 10)}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => onOpen(c)}>Open</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => onQuote(c)}>
              <FileText className="w-3.5 h-3.5 mr-1" /> Quote
            </Button>
            <button className="text-slate-400 hover:text-rose-600 p-1" onClick={() => onDelete(c.id)} aria-label="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// -------- AI Chat --------
function AIChat({ sessionId, pendingPrompt, onPromptConsumed }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey! I can help with cubic-yard math, materials, labor estimates, and anything construction. Ask away." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const sendMessage = async (text) => {
    const msg = text ?? input.trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/ai/chat", { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply || "(empty reply)" }]);
    } catch (e) {
      const msgErr = e?.response?.data?.detail || "AI is unavailable right now. Try again.";
      setMessages((m) => [...m, { role: "assistant", content: `⚠ ${msgErr}` }]);
    } finally {
      setBusy(false);
    }
  };

  // Auto-send prompts coming from "Analyze Results"
  useEffect(() => {
    if (pendingPrompt) {
      sendMessage(pendingPrompt);
      onPromptConsumed && onPromptConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 flex flex-col h-full min-h-[600px]" data-testid="ai-chat">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
        <div className="w-8 h-8 rounded-md bg-emerald-600 flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="font-display font-extrabold text-slate-900">AI Assistant</div>
          <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
            Powered by GPT-5.6 Terra
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ai-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4 text-emerald-700" />
              </div>
            )}
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-blue-600 text-white rounded-tr-sm"
                  : "bg-slate-100 text-slate-900 rounded-tl-sm"
              }`}
              data-testid={`ai-msg-${m.role}`}
            >
              {m.content}
            </div>
            {m.role === "user" && (
              <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <User className="w-4 h-4 text-blue-700" />
              </div>
            )}
          </div>
        ))}
        {busy && (
          <div className="flex gap-2" data-testid="ai-typing">
            <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4 text-emerald-700" />
            </div>
            <div className="bg-slate-100 text-slate-500 rounded-2xl px-3.5 py-2 text-sm flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…
            </div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-1.5" data-testid="ai-suggestions">
          {SUGGESTED_PROMPTS.slice(0, 4).map((p, i) => (
            <button
              key={i}
              onClick={() => sendMessage(p)}
              className="text-xs px-2.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
            >
              {p}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
        className="p-3 border-t border-slate-200 flex gap-2"
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about materials, math, or an estimate…"
          disabled={busy}
          className="h-11"
          data-testid="ai-input"
        />
        <Button type="submit" disabled={busy || !input.trim()} className="h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="ai-send">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

// -------- Save dialog --------
function SaveDialog({ open, onOpenChange, payload, onSaved }) {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const def = payload?.material ? `${payload.material} job` : "New calculation";
      setName(def);
      setNotes("");
    }
  }, [open, payload]);

  const save = async () => {
    if (!name.trim()) { toast.error("Give it a name"); return; }
    setSaving(true);
    try {
      await api.post("/calculations", { ...payload, project_name: name.trim(), notes });
      toast.success("Calculation saved");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="save-calc-dialog">
        <DialogHeader><DialogTitle>Save calculation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Project name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="save-calc-name" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" rows={3} data-testid="save-calc-notes" />
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 rounded-md p-3">
            {payload?.material} · {payload?.length}{payload?.length_unit} × {payload?.width}{payload?.width_unit} × {payload?.depth}{payload?.depth_unit}{" "}
            → <span className="font-mono font-semibold">{payload?.cubic_yards?.toFixed?.(2)} cu yd</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-calc-confirm">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Main page --------
export default function Calculator() {
  const nav = useNavigate();
  const [history, setHistory] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [savePayload, setSavePayload] = useState(null);
  const [pendingPrompt, setPendingPrompt] = useState("");
  const sessionId = useMemo(() => {
    const existing = sessionStorage.getItem("qq_ai_session");
    if (existing) return existing;
    const s = `calc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem("qq_ai_session", s);
    return s;
  }, []);

  const loadHistory = async () => {
    try { const r = await api.get("/calculations"); setHistory(r.data || []); } catch { /* noop */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const createQuote = (payload) => {
    const cy = payload.cubic_yards?.toFixed?.(2) ?? "0";
    const title = `${payload.material} job — ${payload.length}${payload.length_unit} × ${payload.width}${payload.width_unit} × ${payload.depth}${payload.depth_unit}`;
    const desc = `${payload.material} — ${cy} cu yd (${payload.length}${payload.length_unit} × ${payload.width}${payload.width_unit} × ${payload.depth}${payload.depth_unit})`;
    const prefill = {
      title,
      line_items: [
        { description: desc, quantity: Number(cy), unit_price: 0, unit: "cu yd" },
        { description: "Labor & finishing", quantity: 1, unit_price: 0, unit: "job" },
      ],
      notes: `Generated from calculator — Sq Ft: ${payload.square_feet}, Cu Ft: ${payload.cubic_feet}, Cu Yd: ${cy}, Material: ${payload.material}`,
    };
    sessionStorage.setItem("qq_prefill", JSON.stringify(prefill));
    toast.success("Opening quote with your numbers…");
    nav("/dashboard/quotes/new");
  };

  const askSave = (payload) => { setSavePayload(payload); setSaveOpen(true); };

  const analyze = (payload) => {
    const cy = payload.cubic_yards?.toFixed?.(2);
    const prompt =
      `I just calculated this: ${payload.material} job sized ${payload.length}${payload.length_unit} × ${payload.width}${payload.width_unit} × ${payload.depth}${payload.depth_unit}. ` +
      `That's ${payload.square_feet} sq ft, ${payload.cubic_feet} cu ft, ${cy} cu yd. ` +
      `Summarize what to order (including 10% waste), rough labor hours, and anything I might be forgetting.`;
    setPendingPrompt(prompt);
    toast.info("Asking the AI…");
  };

  const openSaved = (c) => {
    toast.message(`Loaded "${c.project_name}" below — tweak and save a new one if needed.`);
    // scroll to top so user sees calc fields; values are retained in-component.
    // We won't force-set inputs to avoid clashing with their current entry.
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const quoteFromSaved = (c) => createQuote({
    length: c.length, width: c.width, depth: c.depth,
    length_unit: c.length_unit, width_unit: c.width_unit, depth_unit: c.depth_unit,
    square_feet: c.square_feet, cubic_feet: c.cubic_feet, cubic_yards: c.cubic_yards,
    material: c.material,
  });

  const deleteSaved = async (id) => {
    if (!window.confirm("Delete this saved calculation?")) return;
    try {
      await api.delete(`/calculations/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="calculator-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Calculator</h1>
        <p className="text-sm text-slate-500 mt-1">
          Everyday math, cubic-yard sizing, and an AI estimator — all on one screen.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-10 gap-5">
        <div className="lg:col-span-7 space-y-5">
          <StandardCalc />
          <CubicYardCalc onCreateQuote={createQuote} onSave={askSave} onAnalyze={analyze} />
          <HistoryPanel items={history} onOpen={openSaved} onQuote={quoteFromSaved} onDelete={deleteSaved} />
        </div>
        <div className="lg:col-span-3">
          <div className="lg:sticky lg:top-20">
            <AIChat
              sessionId={sessionId}
              pendingPrompt={pendingPrompt}
              onPromptConsumed={() => setPendingPrompt("")}
            />
          </div>
        </div>
      </div>

      <SaveDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        payload={savePayload}
        onSaved={loadHistory}
      />
    </div>
  );
}
